import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { useScheduleDays } from "../../../../hooks/useScheduleDays";
import { useScheduleCacheEnabled } from "../../../../hooks/useScheduleWeekRules";
import { toIsoDate } from "../../../../utils/scheduleWeeks";
import { type MonthlyDayData, mergedDaysOfWeeks, monthlyAnchorOf, monthlyDayOfScheduleDay } from "./monthlyJobUtils";

/**
 * The span the window can move in. A window is anchored on the Sunday of the week its first row starts in, as the
 * provider's weeks are, so a change of week start keeps the window on the same weeks.
 */
export type MonthlyRange = {
	/** The earliest anchor a window can have. */
	firstAnchor: DateTime;
	/** The schedule's first day: the staffing panels open on days from here through `lastDay`. */
	firstDay: DateTime;
	/** The last day the view can show: a window whose first row starts after it is out of range. */
	lastDay: DateTime;
};

export type MonthlyDays = {
	/** The days that have arrived, by ISO date. */
	daysByDate: ReadonlyMap<string, MonthlyDayData>;
	/** Undefined while it is unknown, and for a department with no schedule. */
	range: MonthlyRange | undefined;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
};

const byDate = (days: MonthlyDayData[]): ReadonlyMap<string, MonthlyDayData> => new Map(days.map((day) => [toIsoDate(day.date), day]));

/** The provider's weeks are whole Sunday weeks, so their first day is the first anchor. */
const providerRangeOf = (days: MonthlyDayData[]): MonthlyRange | undefined => {
	const firstDay = days.at(0)?.date;
	const lastDay = days.at(-1)?.date;
	if (!firstDay || !lastDay) return undefined;
	return { firstAnchor: firstDay, firstDay, lastDay };
};

/** The department's whole schedule; undefined until a week arrives, and with no schedule. */
const cachedRangeOf = (start: DateTime | null | undefined, end: DateTime | null | undefined, weekStartDay: number): MonthlyRange | undefined =>
	start && end ? { firstAnchor: monthlyAnchorOf(start, weekStartDay), firstDay: start, lastDay: end } : undefined;

/**
 * The Month view's days from `from` through `to`: the schedule cache with `staffing_schedule_cache` on, the
 * provider's loaded weeks with it off. Off, the range is the loaded weeks, so the window moves exactly as it did
 * when it counted positions in them; on, it is the department's whole schedule.
 *
 * Every hook runs in both modes (the flag can turn on while the page is open); each one only does its mode's work.
 * With the flag off `useScheduleDays` asks for nothing.
 */
export const useMonthlyDays = (from: DateTime, to: DateTime, weekStartDay: number): MonthlyDays => {
	const { hospitalData } = useHospitalData();
	const isCacheEnabled = useScheduleCacheEnabled();
	const cached = useScheduleDays(from, to);
	const lastNameFirst = useLastNameFirst();
	const { scheduleStartDate, scheduleEndDate } = cached;

	const providerDays = useMemo(() => mergedDaysOfWeeks(hospitalData.weeks), [hospitalData.weeks]);

	const daysByDate = useMemo(
		() => (isCacheEnabled ? byDate(cached.days.map((day) => monthlyDayOfScheduleDay(day, cached.nurses, lastNameFirst))) : byDate(providerDays)),
		[isCacheEnabled, cached.days, cached.nurses, lastNameFirst, providerDays],
	);
	const range = useMemo(
		() => (isCacheEnabled ? cachedRangeOf(scheduleStartDate, scheduleEndDate, weekStartDay) : providerRangeOf(providerDays)),
		[isCacheEnabled, scheduleStartDate, scheduleEndDate, weekStartDay, providerDays],
	);

	if (isCacheEnabled) return { daysByDate, range, isLoading: cached.isLoading, isError: cached.isError, refetch: cached.refetch };
	// The provider loads before this view renders and reloads itself, so there is nothing here to wait for or retry.
	return { daysByDate, range, isLoading: false, isError: false, refetch: () => undefined };
};
